import { ImageErrorType } from '@pikpak_web_player/extra'
import { UAParser } from 'ua-parser-js'

// ua-parser-js@2.0.0
// https://docs.uaparser.dev/info/os/name.html
const enum OSName {
  IOS = 'iOS',
  ANDROID = 'Android',
  MACOS = 'macOS',
}

/**
 * 格式化时间字符串, 单位ms
 *  */
export const formatSecondString = (second: number) => {
  let seconds = Math.ceil(second)
  let hour = '00'
  let minute = '00'
  if (seconds >= 3600) {
    const num = Math.floor(seconds / 3600)
    hour = String(num).padStart(2, '0')
    seconds -= num * 3600
  }
  if (seconds >= 60) {
    const num = Math.floor(seconds / 60)
    minute = String(num).padStart(2, '0')
    seconds -= num * 60
  }
  return `${hour}:${minute}:${String(seconds).padStart(2, '0')}`
}

/**
 * 将字节（Byte)单位转换为 KB，MB，或者 GB，甚至 TB
 * 整数位不超过1024，超过则升级单位，小数位两位(KB 除外)
 * @param size 单位字节 Bytes 的大小
 * @param fractionDigits 小数的个数
 */
export const storageUnit = (size: number, fractionDigits = 1, space?: boolean): string => {
  const unitSize = 1024
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let deep = 0
  let result = size
  while (deep < units.length && result >= unitSize) {
    result /= unitSize
    deep += 1
  }
  return `${deep ? result.toFixed(fractionDigits) : result}${space ? ' ' : ''}${units[deep]}`
}

export const APP_DEEPLINK_PREFIX = 'pikpakapp://mypikpak.com' as const

// 使用外部播放器打开
/* eslint-disable-next-line */
export const enum EXTERNAL_PLAYER_STEPS {
  SELECT_PLAYER = 'SELECT_PLAYER',
  COPY_LINK_SUCCESS = 'COPY_LINK_SUCCESS',
}

export const enum ExternalPlayerType {
  IINA = 'IINA',
  POTPLAYER = 'PotPlayer',
  OTHER = 'Other',
}

export const externalPlayerListMap = {
  mac: [
    {
      label: ExternalPlayerType.IINA,
      value: 'iina://weblink?url=',
    },
  ],
  win: [
    {
      label: ExternalPlayerType.POTPLAYER,
      value: 'potplayer://',
    },
  ],
}

export const enum PlayErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  NOT_SUPPORT_VIDEO = 'NOT_SUPPORT_VIDEO',
  FREEZE_FILE = 'FREEZE_FILE',
  AUDIO_NETWORK_ERROR = 'AUDIO_NETWORK_ERROR',
  AUDIO_SUPPORT_ERROR = 'AUDIO_SUPPORT_ERROR',
}

export const ErrorType2Code: { [key in string]?: number | string } = {
  [PlayErrorType.FREEZE_FILE]: 205,
  [PlayErrorType.NOT_SUPPORT_VIDEO]: 'not_support',
  [PlayErrorType.AUDIO_SUPPORT_ERROR]: 'not_support',
  [ImageErrorType.NOT_SUPPORT_IMAGE]: 'not_support',
  [PlayErrorType.NETWORK_ERROR]: 'network_error',
  [PlayErrorType.AUDIO_NETWORK_ERROR]: 'network_error',
  [ImageErrorType.NETWORK_ERROR]: 'network_error',
}

// 判断播放错误类型
export const getPlayErrorType = () => {
  const videoDom: HTMLVideoElement | null = document.querySelector('#video-container video')
  const errorMessage = videoDom?.error?.message || ''
  if (/MEDIA_ELEMENT_ERROR/i.test(errorMessage)) return PlayErrorType.NETWORK_ERROR
  return PlayErrorType.NOT_SUPPORT_VIDEO
}

export const getDomain = () => window.location.hostname.replace(/[-\w]+\.(\w+\.\w+)/, '$1') || 'mypikpak.com'

export const isIOS = () => (new UAParser()).getOS().name === OSName.IOS

export const isAndroid = (new UAParser()).getOS().name === OSName.ANDROID

export const isMacOS = () => (new UAParser()).getOS().name === OSName.MACOS

export const isPikPakClient = (ua?: string) => /PikPak/i.test(ua || window.navigator.userAgent)

export const isWindows = () => /windows|win32/i.test(navigator.userAgent)

// 使用 tags 属性判断是否为星标文件
export const isStarFileByTags = (tags?: {
  id: string;
  name: 'STAR',
  type: 0 | 1;
  time: string
}[]): boolean => !!tags?.find(tag => tag.name === 'STAR' && tag.type === 0)

/**
 * 根据后缀判断是否为音频
 * @param extension { string } 后缀
 * @param mimeType { string }
 */
export const isAudio = (
  extension: string,
  mimeType: string,
) => /(wav|midi|cda|mp3|mp3pro|wma|sacd|vqf|ra|voc|au|aif|snd|aac|flac|ape)$/i.test(
  extension,
) || mimeType.startsWith('audio/')

/**
 * 根据后缀判断是否为图片
 * @param extension { string } 文件后缀
 * @param mimeType { string }
 * @returns boolean
 */
export const isImage = (
  extension: string,
  mimeType: string,
) => /(png|jpeg|bmp|jpg|icon|jpe|gif|jpeg2000)$/i.test(extension) || mimeType.startsWith('image/')

/**
 * 根据后缀判断是否为视频
 * @param extension { string } 文件后缀
 * @param mimeType { string }
 * @returns boolean
 */
export const isVideo = (
  extension: string,
  mimeType: string,
) => /(wmv|asf|asx|rm|rmvb|mpg|mpeg|mpe|3gp|mov|mp4|webm|m4v|avi|mkv|flv|f4v|vob|ts|xv)$/i.test(extension)
  || (mimeType.startsWith('video/') && mimeType !== 'video/webp_pipe')

export const injectCssPreventPullToRefresh = (appendTo?: HTMLElement) => {
  // 禁用下拉刷新
  // 右滑返回
  // 双指放大(maybe)
  const style = document.createElement('style')

  style.innerHTML = `
    /* prevent pull-to-refresh for Safari 16+ */
    @media screen and (pointer: coarse) {
      @supports (-webkit-backdrop-filter: blur(1px)) and (overscroll-behavior-y: none)  {
        html {
          min-height: 100.3%;
          overscroll-behavior-y: none;
        }
      }
    }
    /* prevent pull-to-refresh for Safari 9~15 */
    @media screen and (pointer: coarse) {
      @supports (-webkit-backdrop-filter: blur(1px)) and (not (overscroll-behavior-y: none))  {
        html {
          height: 100%;
          overflow: hidden;
        }
        body {
          margin: 0px;
          max-height: 100%;
          overflow: auto;
          -webkit-overflow-scrolling: touch;
        }
      }
    }
    /* prevent pull-to-refresh for Chrome 63+ */
    body{
      overscroll-behavior-y: none;
    }
  `

  if (appendTo) {
    appendTo.appendChild(style)
  } else {
    document.head.appendChild(style)
  }
}

// 两个点之间的距离
export const getDistance = (x: { pageX: number; pageY: number }, y: { pageX: number; pageY: number }) => {
  const a = Math.abs(x.pageX - y.pageX)
  const b = Math.abs(x.pageY - y.pageY)
  return Math.sqrt(a * a + b * b)
}

export const sleep = (ms: number = 0) => new Promise(resolve => {
    setTimeout(resolve, ms)
  })

export * from './client'

export * from './fileStatus'

export * from './cache'

export default {}
